/**
 * ConnectionsPage — ROROS M6 (External Connectivity)
 *
 * Admin-only settings page for managing MCP server connections.
 * Provides:
 *   - List of configured connections (table view)
 *   - Add / Edit connection modal
 *   - Test connection (discovers available tools)
 *   - Enable / Disable toggle per connection
 *   - Per-agent grant management (select connection → show grants below)
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plug, Plus, Trash2, Pencil, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { t } from '@/lib/i18n';
import { apiFetch } from '@/lib/api';
import { useActiveOrgId } from '@/hooks/queries';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { RorosPage } from '@/components/roros/RorosPage';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Table } from '@/components/ui/Table';
import { showToast } from '@/components/ui/Toast';
import type { TableColumn } from '@/components/ui/Table';
import { formatDateTimeStr, formatDate } from '@/lib/localeFormatters';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Connection {
  id: string;
  name: string;
  type: 'mcp';
  status: 'draft' | 'configured' | 'tested' | 'enabled' | 'disabled';
  scope: 'read' | 'read_write';
  tool_count: number;
  last_tested_at: string | null;
  connection_url: string;
  description: string | null;
  credentials: '[REDACTED]' | '[ENCRYPTED]';
}

interface Grant {
  id: string;
  agent_id: string;
  agent_name: string;
  scope_override: 'read' | 'read_write' | null;
  granted_by: string;
  created_at: string;
}

interface Actor {
  id: string;
  name: string;
  actorType: string;
}

interface Tool {
  name: string;
  description?: string;
}

interface ConnectionFormState {
  name: string;
  url: string;
  credentials: string;
  scope: 'read' | 'read_write';
  description: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusBadge(status: Connection['status']): React.ReactNode {
  switch (status) {
    case 'enabled':
      return <Badge variant="success">{status}</Badge>;
    case 'disabled':
      return <Badge variant="secondary">{status}</Badge>;
    case 'tested':
      return <Badge variant="outline">{status}</Badge>;
    case 'configured':
      return <Badge variant="outline">{status}</Badge>;
    case 'draft':
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

function formatLastTested(iso: string | null): string {
  if (!iso) return '—';
  return formatDateTimeStr(iso);
}

// ─── Connection Form Modal ────────────────────────────────────────────────────

interface ConnectionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  connection: Connection | null; // null = create mode
  onSaved: () => void;
}

function ConnectionFormModal({ isOpen, onClose, connection, onSaved }: ConnectionFormModalProps) {
  const isEdit = !!connection;
  const [form, setForm] = useState<ConnectionFormState>({
    name: '',
    url: '',
    credentials: '',
    scope: 'read',
    description: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (connection) {
        setForm({
          name: connection.name,
          url: connection.connection_url,
          credentials: '',
          scope: connection.scope,
          description: connection.description ?? '',
        });
      } else {
        setForm({ name: '', url: '', credentials: '', scope: 'read', description: '' });
      }
    }
  }, [isOpen, connection]);

  const setField = <K extends keyof ConnectionFormState>(key: K, value: ConnectionFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      showToast.error(t('roros.connections.name') + ' and ' + t('roros.connections.url') + ' are required.');
      return;
    }
    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        type: 'mcp',
        connection_url: form.url.trim(),
        scope: form.scope,
        description: form.description.trim() || null,
      };
      if (form.credentials.trim()) {
        payload.credentials = form.credentials.trim();
      }

      if (isEdit && connection) {
        await apiFetch(`/api/v1/connections/${connection.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        showToast.success(t('roros.connections.save') + ' — updated');
      } else {
        await apiFetch('/api/v1/connections', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        showToast.success(t('roros.connections.save') + ' — created');
      }
      onSaved();
      onClose();
    } catch (err: any) {
      showToast.error(err?.message ?? t('roros.connections.save') + ' failed');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('roros.connections.edit') : t('roros.connections.add_connection')}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            {t('roros.connections.cancel')}
          </Button>
          <Button onClick={handleSubmit} isLoading={isSaving} disabled={isSaving}>
            {t('roros.connections.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4 pt-2">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('roros.connections.name')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <Input
            type="text"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder={t('roros.connections.name')}
            autoFocus
          />
        </div>

        {/* Type (read-only — always MCP) */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('roros.connections.type')}
          </label>
          <Input type="text" value="MCP" readOnly className="bg-muted cursor-default" />
        </div>

        {/* URL */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('roros.connections.url')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <Input
            type="url"
            value={form.url}
            onChange={(e) => setField('url', e.target.value)}
            placeholder="https://mcp.example.com/sse"
          />
        </div>

        {/* Credentials */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('roros.connections.credentials')}
          </label>
          {isEdit ? (
            <Input
              type="password"
              value={form.credentials}
              onChange={(e) => setField('credentials', e.target.value)}
              placeholder={t('roros.connections.credentials_existing')}
            />
          ) : (
            <Input
              type="password"
              value={form.credentials}
              onChange={(e) => setField('credentials', e.target.value)}
              placeholder={t('roros.connections.credentials_placeholder')}
            />
          )}
        </div>

        {/* Scope */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('roros.connections.scope')}
          </label>
          <Select
            value={form.scope}
            onChange={(e) => setField('scope', e.target.value as 'read' | 'read_write')}
          >
            <option value="read">Read</option>
            <option value="read_write">Read + Write</option>
          </Select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('roros.connections.description')}
          </label>
          <Input
            type="text"
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder={t('roros.connections.description')}
          />
        </div>
      </div>
    </Modal>
  );
}

// ─── Test Result Modal ────────────────────────────────────────────────────────

interface TestResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  tools: Tool[];
  connectionName: string;
}

function TestResultModal({ isOpen, onClose, tools, connectionName }: TestResultModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('roros.connections.tools_discovered')} — ${connectionName}`}
      size="md"
      footer={
        <Button onClick={onClose}>{t('roros.connections.cancel')}</Button>
      }
    >
      <div className="pt-2">
        {tools.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('roros.connections.no_connections')}</p>
        ) : (
          <ul className="space-y-2">
            {tools.map((tool) => (
              <li key={tool.name} className="rounded-md border border-border px-3 py-2">
                <p className="text-sm font-medium text-foreground">{tool.name}</p>
                {tool.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{tool.description}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

// ─── Grants Panel ─────────────────────────────────────────────────────────────

interface GrantsPanelProps {
  connection: Connection;
}

function GrantsPanel({ connection }: GrantsPanelProps) {
  const queryClient = useQueryClient();
  const [isAddingGrant, setIsAddingGrant] = useState(false);
  const [newAgentId, setNewAgentId] = useState('');
  const [newScopeOverride, setNewScopeOverride] = useState<'' | 'read' | 'read_write'>('');
  const [isSaving, setIsSaving] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const grantsQuery = useQuery({
    queryKey: ['connections', connection.id, 'grants'],
    queryFn: async () => {
      const res = await apiFetch(`/api/v1/connections/${connection.id}/grants`);
      const json = await res.json();
      return (json?.data?.grants ?? []) as Grant[];
    },
  });

  const actorsQuery = useQuery({
    queryKey: ['actors'],
    queryFn: async () => {
      const res = await apiFetch('/api/v1/actors');
      const json = await res.json();
      return (json?.data?.actors ?? json?.actors ?? []) as Actor[];
    },
  });

  const grants: Grant[] = grantsQuery.data ?? [];
  const actors: Actor[] = actorsQuery.data ?? [];

  const handleAddGrant = async () => {
    if (!newAgentId) {
      showToast.error(t('roros.connections.select_agent'));
      return;
    }
    setIsSaving(true);
    try {
      await apiFetch(`/api/v1/connections/${connection.id}/grants`, {
        method: 'POST',
        body: JSON.stringify({
          agent_id: newAgentId,
          ...(newScopeOverride ? { scope_override: newScopeOverride } : {}),
        }),
      });
      showToast.success(t('roros.connections.add_grant') + ' — saved');
      queryClient.invalidateQueries({ queryKey: ['connections', connection.id, 'grants'] });
      setIsAddingGrant(false);
      setNewAgentId('');
      setNewScopeOverride('');
    } catch (err: any) {
      showToast.error(err?.message ?? t('roros.connections.add_grant') + ' failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevokeGrant = async (grant: Grant) => {
    setRevokingId(grant.id);
    try {
      await apiFetch(`/api/v1/connections/${connection.id}/grants/${grant.id}`, {
        method: 'DELETE',
      });
      showToast.success(t('roros.connections.revoke') + ' — done');
      queryClient.invalidateQueries({ queryKey: ['connections', connection.id, 'grants'] });
    } catch (err: any) {
      showToast.error(err?.message ?? t('roros.connections.revoke') + ' failed');
    } finally {
      setRevokingId(null);
    }
  };

  const grantColumns: TableColumn<Grant>[] = [
    {
      key: 'agent_name',
      title: 'Agent',
      render: (_, row) => <span className="text-sm font-medium">{row.agent_name}</span>,
    },
    {
      key: 'scope_override',
      title: t('roros.connections.scope_override'),
      render: (_, row) =>
        row.scope_override ? (
          <Badge variant="outline">{row.scope_override}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">{t('roros.connections.inherited')}</span>
        ),
    },
    {
      key: 'created_at',
      title: 'Granted',
      render: (_, row) => (
        <span className="text-xs text-muted-foreground">{formatDate(row.created_at)}</span>
      ),
    },
    {
      key: 'actions',
      title: '',
      render: (_, row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleRevokeGrant(row)}
          isLoading={revokingId === row.id}
          disabled={revokingId === row.id}
          className="text-error-600 hover:text-error-700"
        >
          {t('roros.connections.revoke')}
        </Button>
      ),
    },
  ];

  return (
    <div className="mt-6 border-t border-border pt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-foreground">
          {t('roros.connections.grants_title')} — {connection.name}
        </h3>
        {!isAddingGrant && (
          <Button size="sm" variant="outline" onClick={() => setIsAddingGrant(true)}>
            <Plus className="h-4 w-4 mr-1.5" aria-hidden />
            {t('roros.connections.add_grant')}
          </Button>
        )}
      </div>

      {/* Add Grant inline form */}
      {isAddingGrant && (
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md border border-border bg-muted/30 p-3">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-foreground mb-1">
              {t('roros.connections.select_agent')}
            </label>
            <Select value={newAgentId} onChange={(e) => setNewAgentId(e.target.value)}>
              <option value="">{t('roros.connections.select_agent')}</option>
              {actors.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </Select>
          </div>
          <div className="min-w-[160px]">
            <label className="block text-xs font-medium text-foreground mb-1">
              {t('roros.connections.scope_override')}
            </label>
            <Select
              value={newScopeOverride}
              onChange={(e) => setNewScopeOverride(e.target.value as '' | 'read' | 'read_write')}
            >
              <option value="">{t('roros.connections.inherited')}</option>
              <option value="read">Read</option>
              <option value="read_write">Read + Write</option>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAddGrant} isLoading={isSaving} disabled={isSaving}>
              {t('roros.connections.save')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setIsAddingGrant(false); setNewAgentId(''); setNewScopeOverride(''); }}
              disabled={isSaving}
            >
              {t('roros.connections.cancel')}
            </Button>
          </div>
        </div>
      )}

      {grantsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading grants…</p>
      ) : grants.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('roros.connections.no_grants')}</p>
      ) : (
        <Table<Grant>
          data={grants}
          columns={grantColumns}
          searchable={false}
          pagination={false}
        />
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ConnectionsPage() {
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ tools: Tool[]; connectionName: string } | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const canManage = hasPermission(PERMISSIONS.ROROS_OVERARCHING_MANAGE);

  const connectionsQuery = useQuery({
    queryKey: ['connections'],
    queryFn: async () => {
      const res = await apiFetch('/api/v1/connections');
      const json = await res.json();
      return (json?.data?.connections ?? []) as Connection[];
    },
    enabled: canManage,
  });

  const connections: Connection[] = connectionsQuery.data ?? [];

  // Sync selectedConnection when data refreshes
  useEffect(() => {
    if (selectedConnection && connectionsQuery.data) {
      const refreshed = connectionsQuery.data.find((c) => c.id === selectedConnection.id);
      if (refreshed) setSelectedConnection(refreshed);
    }
  }, [connectionsQuery.data, selectedConnection]);

  const handleTest = async (connection: Connection) => {
    setTestingId(connection.id);
    try {
      const res = await apiFetch(`/api/v1/connections/${connection.id}/test`, { method: 'POST' });
      const json = await res.json();
      const tools = (json?.data?.tools ?? []) as Tool[];
      setTestResult({ tools, connectionName: connection.name });
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    } catch (err: any) {
      showToast.error(err?.message ?? 'Test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleToggleEnable = async (connection: Connection) => {
    const action = connection.status === 'enabled' ? 'disable' : 'enable';
    setTogglingId(connection.id);
    try {
      await apiFetch(`/api/v1/connections/${connection.id}/${action}`, { method: 'POST' });
      showToast.success(action === 'enable' ? t('roros.connections.enable') : t('roros.connections.disable'));
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    } catch (err: any) {
      showToast.error(err?.message ?? `Failed to ${action}`);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (connection: Connection) => {
    if (!window.confirm(`${t('roros.connections.delete')} "${connection.name}"?`)) return;
    setDeletingId(connection.id);
    try {
      await apiFetch(`/api/v1/connections/${connection.id}`, { method: 'DELETE' });
      showToast.success(t('roros.connections.delete') + ' — done');
      if (selectedConnection?.id === connection.id) setSelectedConnection(null);
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    } catch (err: any) {
      showToast.error(err?.message ?? 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const columns: TableColumn<Connection>[] = [
    {
      key: 'name',
      title: t('roros.connections.name'),
      sortable: true,
      render: (_, row) => (
        <Button
          variant="link"
          className="text-sm font-medium text-primary-600 h-auto p-0 text-left whitespace-normal"
          onClick={() => setSelectedConnection(row.id === selectedConnection?.id ? null : row)}
        >
          {row.name}
        </Button>
      ),
    },
    {
      key: 'type',
      title: t('roros.connections.type'),
      render: (_, row) => <Badge variant="outline">{row.type.toUpperCase()}</Badge>,
    },
    {
      key: 'status',
      title: t('roros.connections.status'),
      render: (_, row) => statusBadge(row.status),
    },
    {
      key: 'scope',
      title: t('roros.connections.scope'),
      render: (_, row) => (
        <span className="text-sm text-muted-foreground">
          {row.scope === 'read_write' ? 'Read + Write' : 'Read'}
        </span>
      ),
    },
    {
      key: 'tool_count',
      title: t('roros.connections.tool_count'),
      render: (_, row) => <span className="text-sm">{row.tool_count}</span>,
    },
    {
      key: 'last_tested_at',
      title: t('roros.connections.last_tested'),
      render: (_, row) => (
        <span className="text-xs text-muted-foreground">{formatLastTested(row.last_tested_at)}</span>
      ),
    },
    {
      key: 'actions',
      title: '',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          {/* Test */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleTest(row)}
            isLoading={testingId === row.id}
            disabled={testingId === row.id || togglingId === row.id}
            title={t('roros.connections.test')}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
          </Button>

          {/* Enable / Disable */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleToggleEnable(row)}
            isLoading={togglingId === row.id}
            disabled={testingId === row.id || togglingId === row.id}
            title={row.status === 'enabled' ? t('roros.connections.disable') : t('roros.connections.enable')}
          >
            {row.status === 'enabled' ? (
              <XCircle className="h-4 w-4 text-error-500" aria-hidden />
            ) : (
              <CheckCircle className="h-4 w-4 text-success-600" aria-hidden />
            )}
          </Button>

          {/* Edit */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setEditingConnection(row); setIsFormOpen(true); }}
            disabled={deletingId === row.id}
            title={t('roros.connections.edit')}
          >
            <Pencil className="h-4 w-4" aria-hidden />
          </Button>

          {/* Delete */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleDelete(row)}
            isLoading={deletingId === row.id}
            disabled={deletingId === row.id}
            className="text-error-600 hover:text-error-700"
            title={t('roros.connections.delete')}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ),
    },
  ];

  if (!canManage) {
    return (
      <RorosPage title={t('roros.connections.page_title')} icon={<Plug className="h-5 w-5" />}>
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-center p-8">
          <h2 className="text-lg font-semibold text-foreground mb-2">Access restricted</h2>
          <p className="text-sm text-muted-foreground max-w-md">
            You need the ROROS Overarching Manage permission to configure connections.
          </p>
        </div>
      </RorosPage>
    );
  }

  return (
    <RorosPage
      title={t('roros.connections.page_title')}
      icon={<Plug className="h-5 w-5" />}
    >
      {/* Header actions */}
      <div className="flex items-center justify-between mb-4 mt-4">
        <p className="text-sm text-muted-foreground">
          {connections.length} connection{connections.length !== 1 ? 's' : ''}
        </p>
        <Button
          size="sm"
          onClick={() => { setEditingConnection(null); setIsFormOpen(true); }}
        >
          <Plus className="h-4 w-4 mr-1.5" aria-hidden />
          {t('roros.connections.add_connection')}
        </Button>
      </div>

      {/* Connections table */}
      <Table<Connection>
        data={connections}
        columns={columns}
        loading={connectionsQuery.isLoading}
        searchable={false}
        pagination={false}
        emptyText={t('roros.connections.no_connections')}
      />

      {/* Grants panel for selected connection */}
      {selectedConnection && (
        <GrantsPanel connection={selectedConnection} />
      )}

      {/* Add / Edit connection modal */}
      <ConnectionFormModal
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingConnection(null); }}
        connection={editingConnection}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['connections'] })}
      />

      {/* Test result modal */}
      {testResult && (
        <TestResultModal
          isOpen={!!testResult}
          onClose={() => setTestResult(null)}
          tools={testResult.tools}
          connectionName={testResult.connectionName}
        />
      )}
    </RorosPage>
  );
}
